import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { IUserInfo, IUserProvider } from '../../interfaces';
import { getEnv } from '../environment';
import { isEqualObject } from '../utils/compare';
import {
  clearFFFStorage as clearStorage,
  getFromStorage,
  SESSION_STORAGE_EVENT,
  SESSION_STORAGE_KEY,
} from '../utils/storage';
import defaultContext from './defaultContext';
import {
  getMembershipData,
  getMembershipFromStorage,
  getUserFromStorage,
  getUserInfo,
  invalidateUserCache,
} from './service/user';

const UserContext = createContext(defaultContext);

// Make sure to use UserProvider one level up from where you want to consume values

// Business logic for UserProvider
// TO USE: <UserProvider><App /></UserProvider>

/* On load we fetch full user info from the deprecated menu/user-data API
(REACT_APP_USER_INFO_URL) and membership state from the user API
(REACT_APP_MEMBERSHIP_INFO_URL). Both are stored and exposed via context. */

const UserProvider = (userProviderProps?: IUserProvider) => {
  const { children } = userProviderProps || { children: null };
  const [userInfo, setUserInfo] = useState<IUserInfo>(getUserFromStorage());
  const [membershipData, setMembershipData] = useState(getMembershipFromStorage());
  const [contextReady, setContextReady] = useState(false);
  const handleSetUserInfo = useCallback((newUserInfo: IUserInfo) => {
    if (!isEqualObject(newUserInfo, userInfo)) {
      setUserInfo(newUserInfo);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // set user info and membership from session storage when "sessionStorage" event is triggered
  useEffect(() => {
    const handleStorage = () => {
      const sessionStorageData = getFromStorage({ key: SESSION_STORAGE_KEY.userInfo });
      if (sessionStorageData) handleSetUserInfo(sessionStorageData);
      setMembershipData(getMembershipFromStorage());
    };

    window.addEventListener(SESSION_STORAGE_EVENT, handleStorage);
    return () => window.removeEventListener(SESSION_STORAGE_EVENT, handleStorage);
  }, [handleSetUserInfo]);

  useEffect(() => {
    const env = getEnv();
    Promise.all([getUserInfo(), getMembershipData({ env })])
      .then(([newUserinfo, newMembership]) => {
        handleSetUserInfo(newUserinfo);
        setMembershipData(newMembership ?? null);
        setContextReady(true);
      })
      .catch((err) => {
        console.log(err);
        setContextReady(true);
      });
  }, [handleSetUserInfo]);

  const displayChildren = contextReady && children;

  return (
    <UserContext.Provider value={{ userInfo, membershipData, clearStorage, invalidateUserCache }}>
      {displayChildren && children}
    </UserContext.Provider>
  );
};

export function useUserContext() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('You must use the User Provider in order to consume this context.');
  }
  return context;
}

export default UserProvider;
